/**
 * Ocean Booking Form Component
 *
 * The main form component for creating and editing Ocean Bookings.
 * Combines all field sections (Header, Parties, Locations, Equipment,
 * References) into a single form. Conditionally renders response
 * sections (Charges, Transport) when carrier response data exists.
 *
 * Used in:
 * - Ocean Booking detail/review pages
 * - OB edit views
 *
 * Features:
 * - React Hook Form integration
 * - Issue-based highlighting for AI-extracted fields
 * - Validation
 * - Submit handling
 */

import { useForm } from 'react-hook-form';
import { useEffect, forwardRef, useImperativeHandle } from 'react';
import OBHeaderFields from './OBHeaderFields';
import OBPartiesFields from './OBPartiesFields';
import OBLocationsFields from './OBLocationsFields';
import OBEquipmentFields from './OBEquipmentFields';
import OBReferencesFields from './OBReferencesFields';
import OBChargesFields from './OBChargesFields';
import OBTransportFields from './OBTransportFields';
import Button from '../common/Button';
import { unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Default values for a new Ocean Booking form
 */
const defaultValues = {
  header: {
    bookingRequestNumber: '',
    expectedDepartureDate: '',
    expectedArrivalDate: '',
    freightPaymentTermCode: '',
    transportDocumentTypeCode: '',
    incoTerms: '',
    receiptTypeAtOrigin: '',
    deliveryTypeAtDestination: '',
    serviceContractReference: '',
    declaredValue: null,
    declaredValueCurrency: '',
    carrierBookingReference: '',
    bookingStatus: '',
    carrierCode: '',
    carrierServiceCode: '',
    carrierServiceName: '',
    transportDocumentReference: ''
  },
  parties: [],
  shipmentLocations: [],
  requestedEquipment: [],
  confirmedEquipment: [],
  references: [],
  charges: [],
  transportPlan: [],
  shipmentCutOffTimes: [],
  feedbacks: []
};

/**
 * OBForm Component
 *
 * @param {Object} props - Component props
 * @param {Object} [props.oceanBooking] - Ocean booking data object (used in detail page)
 * @param {Object} [props.initialData] - Alias for oceanBooking (used in DocumentReviewer)
 * @param {boolean} [props.isEditMode] - Whether the form is in edit mode
 * @param {Function} [props.onSave] - Called when form is submitted (detail page)
 * @param {Function} [props.onSubmit] - Alias for onSave (DocumentReviewer)
 * @param {Array} [props.issues] - Unified issues array from pipeline
 * @param {Object} [props.rawEvaluation] - LLM-as-judge evaluation result (may be double-nested)
 * @param {Object} [props.evaluation] - Alias for rawEvaluation (DocumentReviewer passes this)
 * @param {boolean} [props.disabled] - Whether the form is disabled (already approved)
 * @param {boolean} [props.hideActions] - Whether to hide the bottom action buttons
 * @param {boolean} [props.isSubmitting] - Show loading state on submit button
 * @param {string} [props.submitLabel] - Label for the submit button
 */
const OBForm = forwardRef(function OBForm({
  oceanBooking,
  initialData,
  isEditMode = false,
  onSave,
  onSubmit: onSubmitProp,
  issues = [],
  rawEvaluation,
  evaluation: evaluationProp,
  disabled = false,
  hideActions = false,
  isSubmitting = false,
  submitLabel = 'Save'
}, ref) {
  // Support both prop naming conventions (detail page vs DocumentReviewer)
  const obData = oceanBooking || initialData;
  const submitHandler = onSave || onSubmitProp;
  const evaluation = unwrapEvaluation(rawEvaluation || evaluationProp);

  // Merge ocean booking data with defaults to ensure all required fields exist
  const mergedDefaults = obData ? {
    ...defaultValues,
    ...obData,
    header: { ...defaultValues.header, ...(obData.header || {}) },
    parties: obData.parties || [],
    shipmentLocations: obData.shipmentLocations || [],
    requestedEquipment: obData.requestedEquipment || [],
    confirmedEquipment: obData.confirmedEquipment || [],
    references: obData.references || [],
    charges: obData.charges || [],
    transportPlan: obData.transportPlan || [],
    shipmentCutOffTimes: obData.shipmentCutOffTimes || [],
    feedbacks: obData.feedbacks || []
  } : defaultValues;

  // Initialize react-hook-form with merged data
  const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: mergedDefaults,
    mode: 'onBlur'
  });

  // Reset form when data changes
  useEffect(() => {
    reset(mergedDefaults);
  }, [JSON.stringify(obData)]);

  /**
   * Handle form submission
   */
  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitHandler?.(data);
    } catch (error) {
      console.error('OBForm submission error:', error);
    }
  });

  // Expose submit method to parent via ref (same pattern as POForm)
  useImperativeHandle(ref, () => ({
    submit: () => {
      onSubmit();
    }
  }));

  // Determine whether response sections should be shown
  const hasConfirmedEquipment = (mergedDefaults.confirmedEquipment || []).length > 0;
  const hasTransportPlan = (mergedDefaults.transportPlan || []).length > 0;
  const hasCharges = (mergedDefaults.charges || []).length > 0;
  const hasResponseData = hasConfirmedEquipment || hasTransportPlan || hasCharges;

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* Header Section */}
      <OBHeaderFields
        control={control}
        register={register}
        isEditMode={isEditMode}
        issues={issues}
        evaluation={evaluation}
        errors={errors}
        oceanBooking={mergedDefaults}
      />

      {/* Parties Section */}
      <OBPartiesFields
        control={control}
        register={register}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Locations Section */}
      <OBLocationsFields
        control={control}
        register={register}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Equipment Section */}
      <OBEquipmentFields
        control={control}
        register={register}
        isEditMode={isEditMode}
        issues={issues}
        confirmedEquipment={mergedDefaults.confirmedEquipment}
      />

      {/* References Section */}
      <OBReferencesFields
        control={control}
        register={register}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Response Sections -- only shown when carrier response data exists */}
      {hasCharges && (
        <OBChargesFields
          control={control}
          register={register}
          isEditMode={isEditMode}
          issues={issues}
        />
      )}

      {(hasTransportPlan || (mergedDefaults.shipmentCutOffTimes || []).length > 0 || (mergedDefaults.feedbacks || []).length > 0) && (
        <OBTransportFields
          control={control}
          register={register}
          isEditMode={isEditMode}
          issues={issues}
          shipmentCutOffTimes={mergedDefaults.shipmentCutOffTimes}
          feedbacks={mergedDefaults.feedbacks}
        />
      )}

      {/* Form Actions */}
      {isEditMode && !hideActions && (
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="submit"
            variant="success"
            size="lg"
            loading={isSubmitting}
            disabled={disabled}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
});

export default OBForm;
